import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import * as dayjs from 'dayjs';
import { concat, interval, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, map, take, throttleTime } from 'rxjs/operators';
import { Appointment } from '../shared/model/appointment-book.models';
import { TMaxUpdateInfo } from '../shared/model/TMaxUpdateInfo.model';
import { ConstantsService } from './constants.service';
import { APIResult, WebAPIResult } from './employee.service';
import { EventService } from './event.service';
import { SessionContextService } from './session-context.service';
import { AppMonitoringService } from './app-monitoring.service';
import { UntilDestroy } from '@ngneat/until-destroy';

export class ResourceStateInfo {
  ResourceId: number;
  NetworkId: number;
  State: string = "";
  TimeLeft: number = 0;
  TimeLeftString: string = "";
  ResourceType: number = -1;
  Name: string = "";
}

export class ResourceAvailabilityInfo {
  // Fields returned by API
  public ResourceId: number;
  public ResourceName: string = "";
  public Available: number = 0;
  public ReadyIn: number = 0;
  public NetworkDeviceId: number = -1;
  public UseQueueManager: boolean = false;
  public ResourceType: number = 0;
  public PaymentAfterSession: boolean = false;

  // Fields used in UI
  public ColourHex: string;
  public CustomText: string = "Custom Text";
  public Selectable: boolean = true;
  public Unavailable: boolean = false;
  public Selected: boolean = false;
  public WaitTime: number = 0;
  public State: string;
}

export class ResWorkTime {
  public StartTime: number;
  public EndTime: number;
}

export class ResUnavailable {
  public StartDateTime: Date;
  public EndDateTime: Date;
}

@UntilDestroy({ checkProperties: true})
@Injectable({
  providedIn: 'root'
})
export class ResourceManagerService implements OnDestroy {

  currentTMaxState: TMaxUpdateInfo = null;
  reload: Subject<any> = new Subject();
  getInfo: Subscription;
  subList: Subscription;

  resourceState: ResourceStateInfo[] = [];
  resourceAvailability: ResourceAvailabilityInfo[] = [];

  resourcePollingInterval = 5;  // number of seconds between re-fetch of resource availability

  public static NextInstanceId:number=1;
  public InstanceId:number=0;

  public get GetResourceState() {
    return this.resourceState;
  }

  constructor(private http: HttpClient, public events: EventService, private session: SessionContextService, private appInsights:AppMonitoringService) {    

    this.InstanceId = ResourceManagerService.NextInstanceId;
    ResourceManagerService.NextInstanceId=ResourceManagerService.NextInstanceId+1;

    console.log(`Starting Resource Manager Service ${this.InstanceId}`);
    this.InitFromDefaultData();

    // sub list contains our subscriptions.  If we are about to add some more we need to make sure we have unsubscribed from existing...
    // this.subList?.unsubscribe();
    this.subList = new Subscription();
    // note that the throttle time is the minimum time between emits.  Thus it is valid to have a lower throttle time then polling interval
    // this means any updates due to changes take a maximum of the specified throttle time before they cause the required update of
    // resource availability time
    setTimeout(() => {
      this.subList.add(
        concat(this.reload.pipe(take(1)), this.reload.pipe(debounceTime(300), throttleTime((this.resourcePollingInterval * 1000)-250))).subscribe((_) => {
          // Only get data if branch and employee selected (Ie logged in)
          if (this.session.ctx.BranchId > 0 && this.session.ctx.EmployeeId > 0) {
            // console.log(`GetResourceAvailabilityInfo from instance ${this.InstanceId}`);

            // we only ever want one subscription to GetResourceAvailabilityInfo - so clear this.getInfo before re-assigning
            this.getInfo?.unsubscribe();
            this.getInfo = this.GetResourceAvailabilityInfo(this.session?.ctx?.BranchId || 1)
              .pipe(take(1))
              .subscribe((r) => {
                if (r != null) {
                  this.resourceAvailability = r || [];
                  this.UpdateResourceStateAvailability();
                  this.events.ResourceStateUpdated(this.resourceState);
                }
              });
          }
        })
      );

      // this.subList.push(this.reload);

      this.subList.add(interval(5 * 1000).subscribe(r => {
        // reload Resource Availability data every 5 seconds
        this.reload.next(undefined);
      }));

      this.subList.add(this.events.onResourceWorkTimeUpdated.subscribe(d => {
        // reload Resource Availability data if changed else were
        this.reload.next(undefined);
      }));

    this.subList.add(this.events.onDefaultDataChanged.subscribe(d => {
      // reload Resource Availability data if changed else were
      this.reload.next(undefined);
    }));
  }, 5000);

    this.subList.add(this.events.onDefaultDataChanged.subscribe(d => {
      if (d) this.InitFromDefaultData();
    }));

    this.subList.add(this.events.onBranchChanged.subscribe(d => {
      if (d) this.InitFromDefaultData();
    }));

    this.subList.add(this.events.onTMaxUpdate
      .subscribe(r => {
        // Save Current state
        this.currentTMaxState = r;

        this.UpdateResourceStateAvailability();
        this.events.ResourceStateUpdated(this.resourceState);
      })
    );

    this.subList.add(this.events.onSessionContextChanged
      .subscribe(s => {
        // Remove Current TMax State if we become disconnected
        if (s.HasHWCConnected == false) {
          this.currentTMaxState = null;
          this.resourceState.forEach((i) => {
            if (i.NetworkId > -1)
              i.State = "";
            i.TimeLeft = 0;
          });
        }

        this.UpdateResourceStateAvailability();
        this.events.ResourceStateUpdated(this.resourceState);
      })
    );
  }

  UpdateResourceStateAvailability() {    
    // Update state according to availability
    if (!this.resourceAvailability || this.resourceAvailability.length < 1 || !this.resourceState)
      return;
    
    // if (environment.production == false) {
    //   console.log('UpdateResourceStateAvailability ' + new Date().toLocaleTimeString());
    //   console.log('Resource Availability Data', JSON.stringify(this.resourceAvailability));
    //   console.log('Current TMax State', JSON.stringify(this.currentTMaxState));
    // }

    this.resourceState.forEach(r => {
      var ra = this.resourceAvailability.find(i => i.ResourceId == r.ResourceId);
      if (ra) {
        // Default state if no information
        r.State = "-";
        r.TimeLeft = 0;
        r.TimeLeftString = "";

        if (ra.Available == 0 && ra.ReadyIn==0) {
          r.State = "Unavailable";
          r.TimeLeft = 0;
        }
        else if (ra.NetworkDeviceId) {
          if (this.currentTMaxState != undefined && ra.NetworkDeviceId <= this.currentTMaxState.maxBeds) {
            r.State = this.currentTMaxState.deviceStatus[r.NetworkId - 1];

            if (r.State == "BedDirty")
              r.TimeLeft = 0;
            else
              r.TimeLeft = this.currentTMaxState.timeLeft[r.NetworkId - 1];
          }
          else {
            if (ra.Available==0 && ra.ReadyIn>0)
            {
             r.State="InProgress" ;
             r.TimeLeft = ra.ReadyIn;
            }
            else 
            {
              r.State = "-";
              r.TimeLeft = 0;
            }
          }
        }
        else if (ra.ReadyIn > 0) {
          r.State = "InProgress";
          r.TimeLeft = ra.ReadyIn;
        }

        else if (ra.ReadyIn == 0) {
          r.State = "Ready";
          r.TimeLeft = 0;
        }
      }
      r.TimeLeftString = ConstantsService.SecondsToString(r.TimeLeft);
    });
  }

  isCellAvailableBecauseOfWorkTime(workTime: ResWorkTime[], date: Date, tod: number): boolean {
    // If Branch has no resources then resourceId will be -1 so show time cell as not available.
    if (!workTime || workTime.length < 1)
      return false;

    // For the given date range and resource, checks to see if we can add appointment.
    // We check the ResourceWorkTime (and ResourceUnavailable data but else where)
    // ResourceWorkTime data is split into Date and time of date (Number of seconds from midnight)

    // if current date time found within resource's work time data then OK to create apt.
    var x = workTime.filter(i => tod >= i.StartTime && tod < i.EndTime);
    if (x && x.length > 0)
      return true;
    else
      return false;
  }

  InitFromDefaultData() {
    this.resourcePollingInterval = this.session?.defaultData?.defData?.ResourceAvailabilityPollingInterval || 5;
    this.resourcePollingInterval = this.session?.defaultData?.defData?.ResourceAvailabilityPollingInterval || 5;
    if (this.session.defaultData.defData.Resources) {
      this.resourceState = [];
      this.session.defaultData.defData.Resources.forEach((r) => {
        if (r.Branch == this.session.GetSelectedBranch().BranchId) {
          var rr: ResourceStateInfo = new ResourceStateInfo();
          rr.ResourceId = r.ResourceId;
          rr.NetworkId = r.NetworkId;
          rr.State = "Dirty";
          rr.Name = r.ResourceName;
          rr.ResourceType = r.ResourceTypeId;
          this.resourceState.push(rr);
        }
      });
      this.UpdateResourceStateAvailability();
      this.events.ResourceStateUpdated(this.resourceState);
    }
  }

  ngOnDestroy(): void {    
    this.subList?.unsubscribe();
    this.reload?.unsubscribe();
    this.getInfo?.unsubscribe();
  }

  UpdateResourceStatesFromApts(aptList: Appointment[]) {
    this.events.ResourceStateUpdated(this.resourceState);
    this.reload.next(undefined);
  }

  updateResourceState(resourceId: number, state: string) {
    const r = this.resourceState.find(i => i.ResourceId == resourceId);
    if (r) {
      console.log(`updateResourceState ${resourceId} ${state} `);
      r.State = state;
      this.events.ResourceStateUpdated(this.resourceState);
    }
  }

  public IsResourceReady(ResourceId: number): boolean {

    var r = this.resourceState.find(i => i.ResourceId == ResourceId);
    if (r)
      return (r.State == "Ready" || r.State=="-") ;
    else
      return false;
  }

  public IsResourceInProgress(ResourceId: number): boolean {
    return this.CanStopResource(ResourceId);
  }

  public IsResourceDirty(ResourceId: number): boolean {

    var r = this.resourceState.find(i => i.ResourceId == ResourceId);
    if (r)
      return (r.State == "BedDirty");
    else
      return false;
  }

  public IsResourcePaymentAfterSession(ResourceId: number): boolean {
    return this.resourceAvailability.find((i) => i.ResourceId == ResourceId)?.PaymentAfterSession;
  }

  public IsResourceTypePaymentAfterSession(ResourceTypeId: number): boolean {
    return this.resourceAvailability.find((i) => i.ResourceType == ResourceTypeId)?.PaymentAfterSession
  }

  public CanStopResource(ResourceId: number): boolean {

    var r = this.resourceState.find(i => i.ResourceId == ResourceId);
    if (r)
      return (r.State == "Delay" || r.State == "TimingLampsOn" || r.State == "InProgress");
    else
      return false;
  }

  public CreateResourceWorkingHoursRecords(StartDate: Date, EndDate: Date, StartTime: Date, EndTime: Date, Resources: number[], ClearExistingRecords: boolean, OnlineBooking: boolean): Observable<APIResult> {

    var data = {
      StartDate: StartDate,
      EndDate: EndDate,
      StartTime: StartTime,
      EndTime: EndTime,
      Resources: Resources,
      ClearExistingRecords: ClearExistingRecords,
      OnlineBooking: OnlineBooking,
    };
    return this.http.post<WebAPIResult>('/api/BackOffice/ResourceManager/CreateResourceAvailableHours', data).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return of(result);
      }),
      map((m) => {
        var result: APIResult = new APIResult();
        result.Success = 1;
        result.Msg = m.Msg;
        return result;
      })
    );
  }

  public checkResourceAvailabilityExpiry(branchId: number): Observable<any> {
    const headers = { 'Content-Type': 'application/json' };
    return this.http.get('/api/BackOffice/ResourceManager/GetResourceData/' + branchId.toString(), { headers }).pipe(
      take(1),
      map((resources: any) => {
        if (resources && resources.length > 0) {
          const today = dayjs();
          const tomorrow = today.add(1, 'day');
          const yesterday = today.add(-1, 'day');
          let expiryDate: any;
          let expired: boolean = false;
          resources.forEach((resource) => {
            const hasAvailabilityYesterday = resource.WorkTime.filter((i) => dayjs(i.StartTime) < yesterday).length > 0;
            const hasAvailabilityToday = resource.WorkTime.filter((i) => (dayjs(i.StartTime) > yesterday) && (dayjs(i.StartTime) < tomorrow)).length > 0;
            if (hasAvailabilityYesterday && (!hasAvailabilityToday)) {
              expired = true;
            }
            else {
              if (resource.WorkTime.length > 0) {
                const maxAvailabilityDate = Math.max(...resource.WorkTime.map((w) => dayjs(w.StartTime)));
                if ((maxAvailabilityDate < expiryDate) || (!expiryDate)) {
                  expiryDate = maxAvailabilityDate;
                }
              }
            }
          });
          if (expiryDate && (expiryDate < today.add(6, 'day'))) {
            return { expired, expiryDate: dayjs(expiryDate).format('D MMM') };
          }
          else {
            return { expired, expiryDate: undefined }
          }
        }
        else {
          return { expired: false, expiryDate: undefined }
        }
      }));
  }

  public GetResourceAvailabilityInfo(branchId: number): Observable<ResourceAvailabilityInfo[]> {

    this.appInsights.logEvent("ClientResourceAvailabilityState", [{'tenant': this.session.ctx.CustomerName}, {'dt': new Date()}, {'Branch':branchId} ]   );
    const url = '/api/AptBook/ResourceAvailabilityState/' + branchId.toString();
    return this.http.get<ResourceAvailabilityInfo[]>(url)
      .pipe(
          take(1),
      ); 
  }

  public RetubeBed(BranchId: number, ResourceId: number, notes: string = ""): Observable<boolean> {
    const url = '/api/ResourceMaintenance/Retube';
    var data = {
      BranchId: BranchId,
      ResourceId: ResourceId,
      CompletedOn: new Date(),
      Notes: notes,
    };
    return this.http.post<boolean>(url, data)
      .pipe(take(1));
  }

  
  public DeepCleanBed(dtoCleanBed: DTODeepCleanBed): Observable<boolean> {
    const url = '/api/ResourceMaintenance/DeepClean';

    return this.http.post<boolean>(url, dtoCleanBed).pipe(take(1));
  }

  public GetAppointmentsSinceLastDeepClean(resourceId: number): Observable<number> {
    const url = `/api/ResourceMaintenance/GetAppointmentsSinceLastDeepClean/${resourceId}`;

    return this.http.get<number>(url).pipe(take(1));
  }
}

export class DTODeepCleanBed {
  BranchId: number;
  DeepCleanDateTime: Date;
  Notes: string
  ResourceId: number;
}
